import { getDisplayZone } from 'common/types/GetDisplayZone';
import { DATASET_TANKERS } from '_legacy/models/Datasets';
import EntityNodeTree from './EntityNodeTree';
import {
    isRangesLocation,
    isWorldwideZoneLocation,
} from '_legacy/models/LocationPart';
import store from 'store';

const getLocationFinalName = (location) => {
    var dataset = store.getState()?.user?.group?.datasetId;
    if (dataset == DATASET_TANKERS) {
        return location?.displayName ?? location?.shortName;
    }
    return location?.shortName ?? location?.displayName;
};

class LocationsNodeTree extends EntityNodeTree {
    initializeNodes(
        customLocations,
        knownLocations = [],
        placeholderLocations,
        countries = [],
        field = ''
    ) {
        super.initializeNodes(
            customLocations,
            knownLocations,
            placeholderLocations
        );

        this.initializeLocationNodes(
            customLocations,
            CUSTOM_LOCATION_NODE,
            LocationsNodeTree.CustomLocationPrefix
        );
        this.initializeKnownLocationNodes(knownLocations, countries, field);
        this.initializeLocationNodes(
            placeholderLocations,
            PLACEHOLDER_LOCATION_NODE,
            LocationsNodeTree.PlaceholderLocationPrefix
        );
    }

    initializeKnownLocationNodes(locations, countries, field) {
        // We are pushing countries into known locations for sorting them together
        for (const country of countries) {
            locations.push({
                deskZone: country.zone,
                country: {
                    displayName: country.displayName,
                },
                shortName: '',
            });
        }

        this.sortLocations(locations, field);

        for (const location of locations) {
            if (!location.deskZone) {
                location.deskZone = CUSTOM_LOCATION_NODE;
            }
            if (isWorldwideZoneLocation(location.locationType)) {
                location.deskZone = 'WORLDWIDE';
            }
            if (!this.getNode(getDisplayZone(location.deskZone, field))) {
                this.pushParentNode(getDisplayZone(location.deskZone, field));
            }
            if (
                location.country?.displayName &&
                !this.getNode(
                    `${getDisplayZone(location.deskZone, field)}-${
                        location.country.displayName
                    }`
                )
            ) {
                const parent = this.getNode(
                    getDisplayZone(location.deskZone, field)
                );
                this.pushLeafNode(
                    `${getDisplayZone(location.deskZone, field)}-${
                        location.country.displayName
                    }`,
                    location.country.displayName,
                    parent
                );
            }
            if (location.id) {
                const parent = this.getNode(
                    location.country?.displayName
                        ? `${getDisplayZone(location.deskZone, field)}-${
                              location.country.displayName
                          }`
                        : getDisplayZone(location.deskZone, field)
                );

                const nodeId = `${getDisplayZone(location.deskZone, field)}-${
                    location.id
                }`;
                const node = this.getNode(nodeId);
                if (!node) {
                    this.pushLeafNode(
                        `${getDisplayZone(location.deskZone, field)}-${
                            location.id
                        }`,
                        getLocationFinalName(location),
                        parent
                    );
                }
            }
        }
    }

    sortLocations(locations, field) {
        locations.sort(function (a, b) {
            const getLocationFullName = (location) => {
                let fullName =
                    getDisplayZone(location.deskZone, field) +
                    getOrderingWeight(location);

                if (location.country?.displayName) {
                    fullName += location.country.displayName;
                }
                const locationFullName = getLocationFinalName(location);
                if (locationFullName && locationFullName !== '') {
                    fullName += locationFullName;
                }

                return fullName;
            };

            // Locations inside zones hierarhy should have the next ordering:
            // 1) Worldwide location
            // 2) Country locations
            // 3) Zone locations
            // 4) Range locations
            const getOrderingWeight = (location) => {
                if (isWorldwideZoneLocation(location.locationType)) {
                    return '0';
                }

                if (location.country?.displayName) {
                    return '1';
                }

                if (isRangesLocation(location.locationType)) {
                    return '3';
                }

                return '2';
            };

            return getLocationFullName(a) > getLocationFullName(b) ? 1 : -1;
        });
    }

    initializeLocationNodes(locations, nodeName, prefix) {
        if (locations) {
            this.pushParentNode(nodeName);

            const uniqueLocations = [...new Set(locations)].sort();
            const parent = this.getNode(nodeName);

            uniqueLocations.forEach((location) =>
                this.pushLeafNode(prefix + location, location, parent)
            );
        }
    }

    isChecked(id) {
        if (!this._checkedSet) {
            this._buildCheckedSet();
        }
        return this._checkedSet.has(id);
    }

    // Builds a Set of all `id` values that would return true from the original
    // O(map_size) isChecked scan. Called once after any filter state change;
    // all subsequent per-row lookups in doesFilterPass are O(1).
    _buildCheckedSet() {
        this._checkedSet = new Set();
        for (const [key, node] of this.nodesMap.entries()) {
            if (node.state !== LocationsNodeTree.States.CHECKED) continue;
            // Always add exact key (handles CUSTOM_, PLACEHOLDER_, and direct matches)
            this._checkedSet.add(key);
            // For non-custom nodes also add every "-suffix" substring of the key.
            // doesFilterPass passes ids like "-locationId" or "-countryName" and
            // the original code did node[0].includes(id) to match zone-prefixed keys.
            if (!key.includes(LocationsNodeTree.CustomLocationPrefix)) {
                let idx = key.indexOf('-');
                while (idx !== -1) {
                    this._checkedSet.add(key.substring(idx));
                    idx = key.indexOf('-', idx + 1);
                }
            }
        }
    }

    // Invalidate the cache whenever the tree state changes.
    setCheckbox(key, state) {
        this._checkedSet = null;
        super.setCheckbox(key, state);
    }
}

const CUSTOM_LOCATION_NODE = 'UNKNOWN';
const PLACEHOLDER_LOCATION_NODE = 'PLACEHOLDER';

LocationsNodeTree.CustomLocationNode = CUSTOM_LOCATION_NODE;
LocationsNodeTree.PlaceholderLocationNode = PLACEHOLDER_LOCATION_NODE;
LocationsNodeTree.States = EntityNodeTree.States;
LocationsNodeTree.RootNodeKey = EntityNodeTree.RootNodeKey;
LocationsNodeTree.CustomLocationPrefix = EntityNodeTree.CustomEntityPrefix;
LocationsNodeTree.PlaceholderLocationPrefix =
    EntityNodeTree.PlaceholderEntityPrefix;

export default LocationsNodeTree;
